import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiSearch, FiFileText, FiZap, FiCheck, FiFilter } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { contractTemplatesAPI, parametersAPI, companiesAPI } from '../../services/api';
import { getLocalized } from '../../i18n/getLocalized';
import SearchableSelect from '../SearchableSelect';
import './ContractTemplatePicker.scss';

// Modal template chooser for the contract form's "fill from draft" strip.
//
// A ContractTemplate carries no contract type, company or main group of its
// own — what it does carry is the field schema its mappings were built
// against. A contract type points at the same schema, so the set of types a
// template can actually fill is exactly the types sharing its schema, and the
// group/company facets follow from those types. That is what the filters below
// resolve, which keeps them honest: a template only appears under a contract
// type it can really be used for.
//
// A template with no schema maps core contract fields only, so it fits every
// type — it stays visible under every filter rather than disappearing.

const idOf = (v) => String(v?._id || v || '');

function ContractTemplatePicker({ open, onClose, onSelect, selectedId }) {
  const { t, i18n } = useTranslation();

  const [templates, setTemplates] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [mainGroups, setMainGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      contractTemplatesAPI.list({ limit: 100 }),
      parametersAPI.contractTypes.list({ active: 'true' }),
      parametersAPI.contractMainGroups.list({ active: 'true' }),
      companiesAPI.list(),
    ])
      .then(([tpl, types, groups, comps]) => {
        setTemplates((tpl.data.templates || []).filter(x => x.mappedFieldCount > 0));
        setContractTypes(types.data.items || []);
        setMainGroups(groups.data.items || []);
        setCompanies(comps.data.companies || []);
      })
      .catch(() => toast.error(t('contractForm.templateFill.loadFailed', { defaultValue: 'Failed to load templates' })))
      .finally(() => setLoading(false));
  }, [open, t]);

  // Which contract types each template can fill, keyed by template id. A
  // template without a schema is universal and gets the whole list.
  const typesByTemplate = useMemo(() => {
    const map = new Map();
    for (const tpl of templates) {
      const schemaId = idOf(tpl.fieldSchemaId);
      map.set(tpl._id, schemaId
        ? contractTypes.filter(ct => idOf(ct.fieldSchemaId) === schemaId)
        : contractTypes);
    }
    return map;
  }, [templates, contractTypes]);

  const isUniversal = (tpl) => !idOf(tpl.fieldSchemaId);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((tpl) => {
      if (q && !(`${tpl.name} ${tpl.description || ''}`.toLowerCase().includes(q))) return false;

      // A universal template fits any type, so no facet can exclude it.
      if (isUniversal(tpl)) return true;

      const types = typesByTemplate.get(tpl._id) || [];
      if (typeFilter && !types.some(ct => idOf(ct) === typeFilter)) return false;
      if (groupFilter && !types.some(ct => idOf(ct.mainGroupId) === groupFilter)) return false;
      if (companyFilter && !types.some((ct) => {
        const scope = ct.companyIds || [];
        // An empty scope means the type is available to every company.
        return scope.length === 0 || scope.some(c => idOf(c) === companyFilter);
      })) return false;
      return true;
    });
  }, [templates, typesByTemplate, search, typeFilter, groupFilter, companyFilter]);

  const activeFilters = [typeFilter, companyFilter, groupFilter].filter(Boolean).length
    + (search.trim() ? 1 : 0);

  const clearFilters = () => {
    setSearch(''); setTypeFilter(''); setCompanyFilter(''); setGroupFilter('');
  };

  const typeName = (ct) => getLocalized(ct.name, i18n.language);

  if (!open) return null;

  return (
    <div className="ctp-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="ctp" onClick={e => e.stopPropagation()}>
        <header className="ctp__header">
          <div className="ctp__title">
            <span className="ctp__title-icon"><FiZap size={16} /></span>
            <div>
              <h3>{t('contractForm.templateFill.pickerTitle', { defaultValue: 'Select a template' })}</h3>
              <p>{t('contractForm.templateFill.pickerSubtitle', { defaultValue: 'Search and filter the mapped contract templates.' })}</p>
            </div>
          </div>
          <button className="ctp__close" onClick={onClose} aria-label={t('common.close', { defaultValue: 'Close' })}>
            <FiX size={16} />
          </button>
        </header>

        <div className="ctp__filters">
          <div className="ctp__search">
            <FiSearch size={14} />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('contractForm.templateFill.searchTemplate', { defaultValue: 'Search templates…' })}
            />
            {search && (
              <button className="ctp__search-clear" onClick={() => setSearch('')} aria-label={t('common.clear', { defaultValue: 'Clear' })}>
                <FiX size={13} />
              </button>
            )}
          </div>

          <div className="ctp__filter">
            <SearchableSelect
              value={typeFilter}
              onChange={setTypeFilter}
              placeholder={t('contractForm.templateFill.filterType', { defaultValue: 'Contract type' })}
              options={[
                { value: '', label: t('contractForm.templateFill.allTypes', { defaultValue: 'All contract types' }) },
                ...contractTypes.map(ct => ({ value: ct._id, label: typeName(ct), sublabel: ct.code })),
              ]}
            />
          </div>

          <div className="ctp__filter">
            <SearchableSelect
              value={companyFilter}
              onChange={setCompanyFilter}
              placeholder={t('contractForm.templateFill.filterCompany', { defaultValue: 'Company' })}
              options={[
                { value: '', label: t('contractForm.templateFill.allCompanies', { defaultValue: 'All companies' }) },
                ...companies.map(c => ({ value: c._id, label: c.name, sublabel: c.code })),
              ]}
            />
          </div>

          <div className="ctp__filter">
            <SearchableSelect
              value={groupFilter}
              onChange={setGroupFilter}
              placeholder={t('contractForm.templateFill.filterGroup', { defaultValue: 'Main group' })}
              options={[
                { value: '', label: t('contractForm.templateFill.allGroups', { defaultValue: 'All main groups' }) },
                ...mainGroups.map(g => ({ value: g._id, label: g.name, sublabel: g.code })),
              ]}
            />
          </div>
        </div>

        <div className="ctp__body">
          {loading ? (
            <div className="ctp__state">
              <div className="ctp__spinner" />
              <p>{t('common.loading', { defaultValue: 'Loading…' })}</p>
            </div>
          ) : !filtered.length ? (
            <div className="ctp__state">
              <FiFileText size={22} />
              <p>{templates.length
                ? t('contractForm.templateFill.noMatches', { defaultValue: 'No template matches these filters' })
                : t('contractForm.templateFill.noTemplates', { defaultValue: 'No templates available' })}</p>
              {!!activeFilters && templates.length > 0 && (
                <button className="ctp__clear" onClick={clearFilters}>
                  {t('contractForm.templateFill.clearFilters', { defaultValue: 'Clear filters' })}
                </button>
              )}
            </div>
          ) : (
            <ul className="ctp__list">
              {filtered.map((tpl) => {
                const types = typesByTemplate.get(tpl._id) || [];
                const universal = isUniversal(tpl);
                const shown = universal ? [] : types.slice(0, 3);
                const isSelected = selectedId === tpl._id;
                const choose = () => { onSelect(tpl); onClose(); };
                return (
                  <li key={tpl._id}>
                    <button
                      type="button"
                      className={`ctp__row${isSelected ? ' is-selected' : ''}`}
                      onClick={choose}
                    >
                      <span className="ctp__row-icon"><FiFileText size={15} /></span>
                      <span className="ctp__row-info">
                        <strong>{tpl.name}</strong>
                        {tpl.description && <span className="ctp__row-desc">{tpl.description}</span>}
                        <span className="ctp__row-tags">
                          <span className="ctp__tag ctp__tag--accent">
                            {t('contractForm.templateFill.mappedFields', {
                              count: tpl.mappedFieldCount,
                              defaultValue: '{{count}} fields',
                            })}
                          </span>
                          {universal ? (
                            <span className="ctp__tag">
                              {t('contractForm.templateFill.anyType', { defaultValue: 'Any contract type' })}
                            </span>
                          ) : (
                            <>
                              {shown.map(ct => <span key={ct._id} className="ctp__tag">{typeName(ct)}</span>)}
                              {types.length > shown.length && (
                                <span className="ctp__tag">+{types.length - shown.length}</span>
                              )}
                              {!types.length && (
                                <span className="ctp__tag ctp__tag--muted">
                                  {t('contractForm.templateFill.noLinkedType', { defaultValue: 'No linked contract type' })}
                                </span>
                              )}
                            </>
                          )}
                        </span>
                      </span>
                      {isSelected && <span className="ctp__row-check"><FiCheck size={15} /></span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="ctp__footer">
          <span className="ctp__count">
            <FiFilter size={12} />
            {t('contractForm.templateFill.resultCount', {
              count: filtered.length,
              defaultValue: '{{count}} templates',
            })}
          </span>
          {!!activeFilters && (
            <button className="ctp__clear" onClick={clearFilters}>
              {t('contractForm.templateFill.clearFilters', { defaultValue: 'Clear filters' })}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

export default ContractTemplatePicker;
